import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { Input, SelectForm } from "components";
import Datepicker from "components/Datepicker";
import RadioInput from "components/RadioInput";
import { ErrorHelper, MaskHelper, StringHelper } from "helpers";
import { useBlurHandler, useChangeHandler, usePrefixedSetter } from "helpers/FieldHandlerHelper";
import { PlateConsult } from "models/PlateConsult";
import { TagCompany as ModelTagCompany, TagCompany } from "models/TagCompany";
import { PlateConsultService } from "services/plateTagService";
import { TagCompanyService } from "services/tagCompanyService";

import { useDataModel } from "hooks/useDataModel";
import { applicationDataSections } from ".";
import { Section, SectionContent, SectionHeader, SectionItem } from "../layout";
import { ApplicationDocumentValidationStatus, ApplicationFormik, ApplicationVehicleFields } from "../types";

const applicationVehiclePrefix = "applicationVehicle";

const getNumberInWords = (num: number): string => {
	const numbers = ["", "uma", "duas", "três", "quatro", "cinco", "seis"];

	if (num >= 1 && num <= 6) {
		return numbers[num];
	}

	return num.toString();
};

export default function ApplicationVehicleSection({ formik }: Readonly<ApplicationFormik>): JSX.Element {
	const { applicationVehicle: values } = formik.values;
	const errors = formik.errors?.applicationVehicle;
	const touched = formik.touched?.applicationVehicle;
	const nextYearDate = useMemo(() => new Date(new Date().getFullYear() + 1, 0), []);
	const isValidated = useMemo(
		() => values.documentValidationStatus === ApplicationDocumentValidationStatus.VALIDATION_SUCCESS,
		[values.documentValidationStatus]
	);
	const [isLoadingTagCompany, setIsLoadingTagCompany] = useState<boolean>(false);
	const [isConsultingPlate, setIsConsultingPlate] = useState<boolean>(false);
	const [isConsultingPlateError, setIsConsultingPlateError] = useState<boolean>(false);
	const [tagCompanies, setTagCompanies] = useState<ModelTagCompany[] | null>(null);
	const [plateTags, setPlateTags] = useState<PlateConsult[] | null>(null);
	const [radioValue, setRadioValue] = useState<string>("");
	const debounceRef = useRef<number | null>(null);
	const setApplicationFieldValue = usePrefixedSetter(applicationVehiclePrefix, formik.setFieldValue);
	const handleChange = useChangeHandler(setApplicationFieldValue);
	const setApplicationFieldTouched = usePrefixedSetter(applicationVehiclePrefix, formik.setFieldTouched);
	const handleBlur = useBlurHandler(setApplicationFieldTouched);
	const selectedTagCompany = useDataModel(values.tagCompany, TagCompany);
	const tagCompanyService = useMemo(TagCompanyService.getInstance, []);
	const plateConsultService = useMemo(PlateConsultService.getInstance, []);

	useEffect(() => {
		setIsLoadingTagCompany(true);
		tagCompanyService
			.listAll()
			.then((response) => setTagCompanies(response))
			.catch((e) => toast.error(ErrorHelper.getResponseErrorMessage(e)))
			.finally(() => setIsLoadingTagCompany(false));
	}, [tagCompanyService]);

	useEffect(() => {
		setIsLoadingTagCompany(true);
		tagCompanyService
			.listAll()
			.then((response) => {
				setTagCompanies(response);
			})
			.catch((e) => toast.error(ErrorHelper.getResponseErrorMessage(e)))
			.finally(() => setIsLoadingTagCompany(false));
	}, []);

	useEffect(() => {
		const plate = values.plate?.trim().toUpperCase() || "";

		setRadioValue("");
		setApplicationFieldValue(ApplicationVehicleFields.tagCompany, null);
		setApplicationFieldValue(ApplicationVehicleFields.tagNumber, null);
		setApplicationFieldValue(ApplicationVehicleFields.tagSelection, null);
		if (debounceRef.current) window.clearTimeout(debounceRef.current);

		if (plate.length === 7) {
			setIsConsultingPlate(true);
			debounceRef.current = window.setTimeout(() => {
				plateConsultService
					.consultPlate(plate)
					.then((results) => {
						setPlateTags(results);
						const setCompanies = new Set<string>();
						(results || []).forEach((t) => {
							if (t.empresa) setCompanies.add(t.empresa);
						});
						setIsConsultingPlateError(false);

						const count = setCompanies.size;
						setApplicationFieldValue(ApplicationVehicleFields.foundPlateTagsCount, count);
						if (count === 0) {
							setApplicationFieldValue(ApplicationVehicleFields.tagSelection, "none");
						}
					})
					.catch((e) => {
						setPlateTags(null);
						const isServerError = e?.response?.status >= 500 && e?.response?.status < 600;
						setIsConsultingPlateError(isServerError);
						setApplicationFieldValue(ApplicationVehicleFields.foundPlateTagsCount, 0);
					})
					.finally(() => setIsConsultingPlate(false));
			}, 500);
		} else {
			setPlateTags(null);
			setIsConsultingPlate(false);
			setApplicationFieldValue(ApplicationVehicleFields.foundPlateTagsCount, 0);
			setApplicationFieldValue(ApplicationVehicleFields.tagSelection, null);
		}

		return () => {
			if (debounceRef.current) window.clearTimeout(debounceRef.current);
		};
	}, [values.plate]);

	const findCompanyByName = (name: string): ModelTagCompany | undefined => {
		const slug = StringHelper.slugify(name || "");
		return tagCompanies?.find((c) => StringHelper.slugify(c.name || "") === slug);
	};

	useEffect(() => {
		if (!values.tagNumber || values.tagCompany || !plateTags) return;
		const matchedPlateTag = plateTags.find((t) => t.tag === values.tagNumber && t.empresa);
		if (!matchedPlateTag?.empresa) return;
		const company = findCompanyByName(matchedPlateTag.empresa);
		if (company) {
			setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
				id: company.id,
				name: company.name,
				code: company.code
			});
			return;
		}
		setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
			id: matchedPlateTag.empresa,
			name: matchedPlateTag.empresa,
			code: ""
		});
	}, [values.tagNumber, values.tagCompany, plateTags, tagCompanies]);

	const isFieldDisabled = (field: ApplicationVehicleFields) => {
		const originally = isValidated && !values.fieldsMissingFromApi?.includes(field);
		if (isConsultingPlate) return true;
		return originally;
	};

	const uniqueCompaniesFromPlate = useMemo(() => {
		if (!plateTags?.length) return [];
		const set = new Set<string>();
		plateTags.forEach((t) => {
			if (t.empresa) set.add(t.empresa);
		});
		return Array.from(set);
	}, [plateTags]);

	const renderedCompanies = useMemo(
		() =>
			uniqueCompaniesFromPlate.map((companyName) => {
				const matched = findCompanyByName(companyName);
				return {
					label: companyName,
					value: matched?.id || companyName,
					matched
				};
			}),
		[uniqueCompaniesFromPlate, tagCompanies]
	);

	const handleSingleTagAnswer = (value: string) => {
		setRadioValue(value);
		setApplicationFieldValue(ApplicationVehicleFields.tagSelection, value);
		const companyName = uniqueCompaniesFromPlate[0];
		if (!companyName) return;
		if (value === "yes") {
			const company = findCompanyByName(companyName);
			const firstTag = plateTags?.find((t) => t.empresa && StringHelper.slugify(t.empresa) === StringHelper.slugify(companyName));
			if (company) {
				setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
					id: company.id,
					name: companyName,
					code: company.code
				});
			} else {
				setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
					id: null,
					name: companyName,
					code: ""
				});
			}
			setApplicationFieldValue(ApplicationVehicleFields.tagNumber, firstTag?.tag || null);
		} else {
			setApplicationFieldValue(ApplicationVehicleFields.tagCompany, null);
			setApplicationFieldValue(ApplicationVehicleFields.tagNumber, null);
		}
	};

	const handleMultipleTagAnswer = (value: string) => {
		setRadioValue(value);
		setApplicationFieldValue(ApplicationVehicleFields.tagSelection, value);
		if (value === "none") {
			setApplicationFieldValue(ApplicationVehicleFields.tagCompany, null);
			setApplicationFieldValue(ApplicationVehicleFields.tagNumber, null);
			return;
		}

		let company = tagCompanies?.find((c) => c.id === value);
		if (!company) {
			const matchByName = tagCompanies?.find((c) => StringHelper.slugify(c.name || "") === StringHelper.slugify(value));
			if (matchByName) company = matchByName;
		}

		let correspondingTag: PlateConsult | undefined;

		if (company) {
			correspondingTag = plateTags?.find((t) => StringHelper.slugify(t.empresa || "") === StringHelper.slugify(company?.name || ""));
		}
		if (!correspondingTag) {
			correspondingTag = plateTags?.find((t) => StringHelper.slugify(t.empresa || "") === StringHelper.slugify(value));
		}
		if (correspondingTag) {
			if (company) {
				setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
					id: company.id,
					name: correspondingTag.empresa,
					code: company.code
				});
			} else {
				setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
					id: null,
					name: correspondingTag.empresa,
					code: ""
				});
			}
			setApplicationFieldValue(ApplicationVehicleFields.tagNumber, correspondingTag.tag);
		} else {
			console.warn("Tag correspondente não encontrada para:", value);
			setApplicationFieldValue(ApplicationVehicleFields.tagCompany, {
				id: null,
				name: value,
				code: ""
			});
			setApplicationFieldValue(ApplicationVehicleFields.tagNumber, null);
		}
	};

	useEffect(() => {
		if (!radioValue) return;
		if (values.tagCompany) return;
		if (uniqueCompaniesFromPlate.length === 1 && radioValue === "yes") {
			handleSingleTagAnswer("yes");
			return;
		}
		if (uniqueCompaniesFromPlate.length > 1 && radioValue !== "none") {
			handleMultipleTagAnswer(radioValue);
		}
	}, [radioValue, values.tagCompany, uniqueCompaniesFromPlate.length]);

	return (
		<Section>
			<SectionHeader title="Dados do veículo" />
			<SectionContent id={`section-${applicationDataSections.applicationVehicle}`}>
				<SectionItem colSpan={12}>
					<Input
						type="text"
						name={ApplicationVehicleFields.plate}
						label="Placa do veículo"
						value={values.plate}
						error={errors?.plate}
						touched={touched?.plate}
						onChange={(evt) => handleChange(evt, MaskHelper.alphanumericUpperCase)}
						onBlur={handleBlur}
						placeholder="Digite"
						maxLength={7}
						disabled={isFieldDisabled(ApplicationVehicleFields.plate)}
						isLoading={isConsultingPlate}
					/>
				</SectionItem>

				{uniqueCompaniesFromPlate.length === 1 && (
					<SectionItem colSpan={12}>
						<div className="border border-neutral-high-200 rounded-lg p-4">
							<div className="mb-2">
								<span className="font-medium">
									Encontramos uma tag da empresa <strong>{uniqueCompaniesFromPlate[0]}</strong> registrada para este veículo.
								</span>
								<span className="text-feedback-negative-pure-500"> *</span>
								{(() => {
									const firstTag = plateTags?.find(
										(t) => t.empresa && StringHelper.slugify(t.empresa) === StringHelper.slugify(uniqueCompaniesFromPlate[0])
									);
									return firstTag?.tag && firstTag.tag !== "N/A" ? (
										<div className="mt-1 text-sm text-neutral-low-400">
											Número da tag: <strong>{firstTag.tag}</strong>
										</div>
									) : null;
								})()}
							</div>
							<RadioInput
								name="plate-tag-single"
								value={radioValue}
								onChange={(e) => handleSingleTagAnswer(e.currentTarget.value)}
								options={[
									{ label: "SIM, meu veículo possui esta tag.", value: "yes" },
									{ label: "NÃO, meu veículo não possui esta tag.", value: "no" }
								]}
								layout="vertical"
								isRequired={false}
								disabled={isConsultingPlate}
							/>
						</div>
					</SectionItem>
				)}
				{uniqueCompaniesFromPlate.length > 1 && (
					<SectionItem colSpan={12}>
						<div className="border border-neutral-high-200 rounded-lg p-4">
							<RadioInput
								name="plate-tag-multiple"
								label={`Encontramos ${getNumberInWords(
									uniqueCompaniesFromPlate.length
								)} TAGS registradas para este veículo. Escolha uma opção para prosseguir:`}
								value={radioValue}
								onChange={(e) => handleMultipleTagAnswer(e.currentTarget.value)}
								options={[
									...renderedCompanies.map((c) => ({ label: c.label, value: c.value })),
									{ label: "O veículo não tem TAG", value: "none" }
								]}
								layout="vertical"
								disabled={isConsultingPlate}
							/>
						</div>
					</SectionItem>
				)}

				<SectionItem colSpan={6}>
					<Input
						type="text"
						name={ApplicationVehicleFields.renavamCode}
						label="RENAVAM"
						value={values.renavamCode}
						error={errors?.renavamCode}
						touched={touched?.renavamCode}
						onChange={(evt) => handleChange(evt, MaskHelper.onlyNumbers)}
						onBlur={handleBlur}
						placeholder="Digite"
						maxLength={11}
						disabled={isFieldDisabled(ApplicationVehicleFields.renavamCode)}
					/>
				</SectionItem>
				<SectionItem colSpan={6}>
					<Input
						type="text"
						name={ApplicationVehicleFields.color}
						label="Cor do veículo"
						value={values.color}
						error={errors?.color}
						touched={touched?.color}
						onChange={handleChange}
						onBlur={handleBlur}
						placeholder="Digite"
						disabled={isFieldDisabled(ApplicationVehicleFields.color)}
					/>
				</SectionItem>
				<SectionItem colSpan={6}>
					<Input
						type="text"
						name={ApplicationVehicleFields.vehicleBrand}
						label="Marca do veículo"
						value={values.vehicleBrand}
						error={errors?.vehicleBrand}
						touched={touched?.vehicleBrand}
						onChange={handleChange}
						onBlur={handleBlur}
						placeholder="Digite"
						disabled={isFieldDisabled(ApplicationVehicleFields.vehicleBrand)}
						required={false}
					/>
				</SectionItem>
				<SectionItem colSpan={6}>
					<Input
						type="text"
						name={ApplicationVehicleFields.vehicleModel}
						label="Modelo do veículo"
						value={values.vehicleModel}
						error={errors?.vehicleModel}
						touched={touched?.vehicleModel}
						onChange={handleChange}
						onBlur={handleBlur}
						placeholder="Digite"
						disabled={isFieldDisabled(ApplicationVehicleFields.vehicleModel)}
					/>
				</SectionItem>
				<SectionItem colSpan={6}>
					<Datepicker
						label="Ano fabricação"
						selectedDate={values.manufactureYear ? new Date(values.manufactureYear, 0) : null}
						onChange={(date) => setApplicationFieldValue(ApplicationVehicleFields.manufactureYear, date)}
						maxDate={new Date()}
						onBlur={handleBlur}
						error={errors?.manufactureYear}
						touched={touched?.manufactureYear}
						showYearPicker
						disabled={isFieldDisabled(ApplicationVehicleFields.manufactureYear)}
					/>
				</SectionItem>
				<SectionItem colSpan={6}>
					<Datepicker
						label="Ano modelo"
						selectedDate={values.modelYear ? new Date(values.modelYear, 0) : null}
						onChange={(date) => setApplicationFieldValue(ApplicationVehicleFields.modelYear, date)}
						maxDate={nextYearDate}
						onBlur={handleBlur}
						error={errors?.modelYear}
						touched={touched?.modelYear}
						showYearPicker
						disabled={isFieldDisabled(ApplicationVehicleFields.modelYear)}
					/>
				</SectionItem>
				{isConsultingPlateError && (
					<>
						<SectionItem colSpan={6}>
							<Input
								type="text"
								name={ApplicationVehicleFields.tagNumber}
								label="Número da TAG"
								value={values.tagNumber}
								error={errors?.tagNumber}
								touched={touched?.tagNumber}
								onChange={(evt) => handleChange(evt, MaskHelper.onlyNumbers)}
								onBlur={(e) => {
									if (!e.target.value) {
										setApplicationFieldValue(ApplicationVehicleFields.tagNumber, null);
									}
									handleBlur(e);
								}}
								placeholder="Digite"
								maxLength={10}
								required={false}
								disabled={isConsultingPlate}
							/>
						</SectionItem>
						<SectionItem colSpan={6}>
							<SelectForm
								options={tagCompanies ? tagCompanies!.map((type) => type.toOption()) : []}
								name={ApplicationVehicleFields.tagCompany}
								label="Empresa responsável"
								value={selectedTagCompany?.toOption()}
								setFieldValue={(field, value) => setApplicationFieldValue(field, value)}
								useOuterScroll
								isLoading={isLoadingTagCompany}
								isDisabled={isLoadingTagCompany}
								menuPosition="absolute"
								menuPlacement="top"
								noOptionsMessage="Nenhuma empresa responsável encontrada"
								error={errors?.tagCompany}
								touched={touched?.tagCompany}
								isRequired={false}
							/>
						</SectionItem>
					</>
				)}
			</SectionContent>
		</Section>
	);
}
